(() => {
  'use strict';

  if (window.__PICCO_FAVOURITES_UNIVERSAL__) {
    if (typeof window.__PICCO_FAVOURITES_UNIVERSAL__.sync === 'function') {
      window.__PICCO_FAVOURITES_UNIVERSAL__.sync();
    }
    return;
  }

  const CANONICAL_KEY = 'picco_favourites_v1';
  const DETAIL_KEY = 'picco_favourites_items';
  const LEGACY_ID_KEYS = [
    'picco_iwp_wishlist',
    'picco-reference-wishlist',
    'picco_rf_wishlist'
  ];
  const MAX_STORED = 200;

  const BUTTON_SELECTOR = [
    '[data-wishlist]',
    '[data-iwp-wishlist]',
    '[data-iwp-quick-wishlist]',
    '[data-picco-quick-wishlist]',
    '[data-favourite-button]',
    '[data-quick-wishlist]',
    '[data-picco-rvf-wish]',
    '[data-picco-rvf-quick-wishlist]',
    '[data-pc-quick-save]',
    '[data-pc-wishlist]',
    '[data-pc-save]',
    '[data-wishlist-button]',
    'wishlist-button',
    'button[aria-label*="favourite" i]',
    'button[aria-label*="favorite" i]'
  ].join(',');

  const IGNORE_SELECTOR = [
    '[data-pf-remove]',
    '.picco-favourites__save'
  ].join(',');

  const safeParse = (value, fallback) => {
    try {
      const parsed = JSON.parse(value);
      return parsed == null ? fallback : parsed;
    } catch (_) {
      return fallback;
    }
  };

  const unique = (items) => Array.from(new Set(items.filter(Boolean)));

  const normalizeHandle = (value) => {
    if (!value) return '';
    let handle = String(value).trim();

    try {
      if (handle.includes('/products/')) {
        const url = new URL(handle, window.location.origin);
        const match = url.pathname.match(/\/products\/([^/?#]+)/i);
        if (match) handle = match[1];
      }
    } catch (_) {}

    return handle
      .replace(/^.*\/products\//i, '')
      .replace(/[?#].*$/, '')
      .replace(/^\/+|\/+$/g, '')
      .trim();
  };

  const productUrlFromHandle = (handle) => {
    const clean = normalizeHandle(handle);
    if (!clean) return '';
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}products/${encodeURIComponent(clean)}`;
  };

  const readCanonical = () => {
    const value = safeParse(localStorage.getItem(CANONICAL_KEY), []);
    return Array.isArray(value)
      ? unique(value.map(normalizeHandle)).slice(0, MAX_STORED)
      : [];
  };

  const writeCanonical = (handles) => {
    const clean = unique((handles || []).map(normalizeHandle)).slice(0, MAX_STORED);
    try { localStorage.setItem(CANONICAL_KEY, JSON.stringify(clean)); } catch (_) {}
    return clean;
  };

  const readDetails = () => {
    const value = safeParse(localStorage.getItem(DETAIL_KEY), []);
    if (!Array.isArray(value)) return [];
    return value
      .filter((item) => item && typeof item === 'object')
      .map((item) => ({
        id: item.id == null ? '' : String(item.id),
        handle: normalizeHandle(item.handle || item.url || ''),
        title: String(item.title || ''),
        url: String(item.url || '')
      }))
      .filter((item) => item.handle || item.id)
      .slice(0, MAX_STORED);
  };

  const writeDetails = (items) => {
    const byHandle = new Map();
    (items || []).forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const handle = normalizeHandle(item.handle || item.url || '');
      if (!handle) return;
      const current = byHandle.get(handle) || {};
      byHandle.set(handle, {
        id: item.id == null || item.id === '' ? String(current.id || '') : String(item.id),
        handle,
        title: String(item.title || current.title || ''),
        url: String(item.url || current.url || productUrlFromHandle(handle))
      });
    });
    const clean = Array.from(byHandle.values()).slice(0, MAX_STORED);
    try { localStorage.setItem(DETAIL_KEY, JSON.stringify(clean)); } catch (_) {}
    return clean;
  };

  const readLegacyIds = (key) => {
    const value = safeParse(localStorage.getItem(key), []);
    if (!Array.isArray(value)) return [];
    return unique(value.map((item) => {
      if (item && typeof item === 'object' && item.id != null) return String(item.id);
      return String(item || '');
    }).filter(Boolean));
  };

  const writeLegacyIds = (key, ids) => {
    const clean = unique((ids || []).map(String)).slice(0, MAX_STORED);
    try { localStorage.setItem(key, JSON.stringify(clean)); } catch (_) {}
    return clean;
  };

  const closestProductScope = (element) => {
    if (!element || typeof element.closest !== 'function') return null;
    return element.closest([
      '[data-product-handle]',
      '[data-product-url]',
      '[data-product-id]',
      '[data-product-card]',
      '[data-picco-drawer]',
      '[data-pc-quick-shell]',
      '[data-picco-rvf-quick]',
      '[data-quick-shop]',
      'article',
      'li'
    ].join(','));
  };

  const firstProductLink = (start) => {
    const scopes = [];
    if (start) scopes.push(start);
    const immediate = closestProductScope(start);
    if (immediate && immediate !== start) scopes.push(immediate);

    let node = start;
    for (let i = 0; node && i < 8; i += 1, node = node.parentElement) {
      if (!scopes.includes(node)) scopes.push(node);
    }

    for (const scope of scopes) {
      if (!scope || typeof scope.querySelector !== 'function') continue;
      const link = scope.querySelector('a[href*="/products/"]');
      if (link && link.getAttribute('href')) return link;
    }

    return null;
  };

  const extractContext = (button) => {
    if (!button) return { id: '', handle: '', title: '', url: '' };

    const scope = closestProductScope(button) || button.parentElement;
    const chain = [button];
    let node = scope;
    for (let i = 0; node && i < 8; i += 1, node = node.parentElement) chain.push(node);

    let id = '';
    let handle = '';
    let title = '';
    let url = '';

    const readDataset = (el) => {
      if (!el || !el.dataset) return;
      if (!id) id = String(el.dataset.productId || el.dataset.id || '');
      if (!handle) {
        handle = normalizeHandle(
          el.dataset.productHandle ||
          el.dataset.handle ||
          el.dataset.piccoRvfWish ||
          el.dataset.wishlistHandle ||
          el.dataset.productUrl ||
          ''
        );
      }
      if (!title) title = String(el.dataset.productTitle || el.dataset.title || '');
      if (!url) url = String(el.dataset.productUrl || '');
    };

    chain.forEach(readDataset);

    const productLink = firstProductLink(scope || button);
    if (productLink) {
      const href = productLink.getAttribute('href') || '';
      if (!url) url = href;
      if (!handle) handle = normalizeHandle(href);
      if (!title) {
        title = String(
          productLink.getAttribute('aria-label') ||
          productLink.getAttribute('title') ||
          productLink.textContent ||
          ''
        ).trim();
      }
    }

    if (!handle && url) handle = normalizeHandle(url);

    if (!handle && location.pathname.includes('/products/')) {
      handle = normalizeHandle(location.pathname);
      if (!url) url = location.pathname;
      if (!title) title = document.querySelector('h1')?.textContent?.trim() || '';
    }

    if (!url && handle) url = productUrlFromHandle(handle);

    if (!title && scope && typeof scope.querySelector === 'function') {
      title = String(
        scope.querySelector('[data-product-title], .picco-product-card__title, .picco-ref-card__title, .picco-rf__title, .pc-card__title, h3, h2')?.textContent ||
        ''
      ).trim();
    }

    return { id, handle, title, url };
  };

  const legacyContainsId = (id) => {
    if (!id) return false;
    return LEGACY_ID_KEYS.some((key) => readLegacyIds(key).includes(String(id)));
  };

  const mergeVisibleLegacySaves = () => {
    let handles = readCanonical();
    const details = readDetails();

    details.forEach((item) => {
      if (item.handle && !handles.includes(item.handle)) handles.push(item.handle);
    });

    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      if (button.matches(IGNORE_SELECTOR)) return;
      const ctx = extractContext(button);
      if (!ctx.handle) return;
      const nativePressed = button.getAttribute('aria-pressed') === 'true';
      if (nativePressed || legacyContainsId(ctx.id)) {
        if (!handles.includes(ctx.handle)) handles.unshift(ctx.handle);
        upsertDetail(ctx, false);
      }
    });

    handles = writeCanonical(handles);
    return handles;
  };

  const upsertDetail = (ctx, shouldWrite = true) => {
    const handle = normalizeHandle(ctx && ctx.handle);
    if (!handle) return readDetails();
    const details = readDetails();
    const index = details.findIndex((item) => item.handle === handle);
    const previous = index >= 0 ? details[index] : {};
    const next = {
      id: String((ctx && ctx.id) || previous.id || ''),
      handle,
      title: String((ctx && ctx.title) || previous.title || ''),
      url: String((ctx && ctx.url) || previous.url || productUrlFromHandle(handle))
    };
    if (index >= 0) details[index] = next;
    else details.unshift(next);
    return shouldWrite ? writeDetails(details) : details;
  };

  const removeDetail = (handle) => {
    const clean = normalizeHandle(handle);
    if (!clean) return;
    writeDetails(readDetails().filter((item) => item.handle !== clean));
  };

  const mirrorLegacyIdState = (id, active) => {
    const cleanId = String(id || '');
    if (!cleanId) return;
    LEGACY_ID_KEYS.forEach((key) => {
      let ids = readLegacyIds(key);
      ids = active
        ? [cleanId, ...ids.filter((item) => item !== cleanId)]
        : ids.filter((item) => item !== cleanId);
      writeLegacyIds(key, ids);
    });
  };

  const fetchAndHydrate = async (ctx, active) => {
    if (!ctx || !ctx.handle || !active) return ctx;
    if (ctx.id && ctx.title) return ctx;

    try {
      const response = await fetch(`${productUrlFromHandle(ctx.handle)}.js`, {
        credentials: 'same-origin',
        cache: 'force-cache',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return ctx;
      const product = await response.json();
      const hydrated = {
        id: String(ctx.id || product.id || ''),
        handle: normalizeHandle(ctx.handle || product.handle || ''),
        title: String(ctx.title || product.title || ''),
        url: String(ctx.url || productUrlFromHandle(ctx.handle || product.handle || ''))
      };
      upsertDetail(hydrated);
      mirrorLegacyIdState(hydrated.id, true);
      return hydrated;
    } catch (_) {
      return ctx;
    }
  };

  const emitChange = (handles, context) => {
    const detail = { handles: handles.slice(), context: context || null };
    document.dispatchEvent(new CustomEvent('picco:wishlist:change', { detail }));
    document.dispatchEvent(new CustomEvent('picco:favourites:changed', { detail }));
    window.dispatchEvent(new CustomEvent('picco:favourites:changed', { detail }));
  };

  const setFavourite = (ctx, active, emit = true) => {
    const handle = normalizeHandle(ctx && ctx.handle);
    if (!handle) return readCanonical();

    let handles = readCanonical();
    handles = active
      ? [handle, ...handles.filter((item) => item !== handle)]
      : handles.filter((item) => item !== handle);
    handles = writeCanonical(handles);

    if (active) upsertDetail({ ...ctx, handle });
    else removeDetail(handle);

    mirrorLegacyIdState(ctx && ctx.id, active);

    if (active) fetchAndHydrate({ ...ctx, handle }, true);
    if (emit) emitChange(handles, { ...ctx, handle, active });
    syncButtons();
    return handles;
  };

  const inferPressedState = (button, ctx) => {
    const pressed = button.getAttribute('aria-pressed');
    if (pressed === 'true') return true;
    if (pressed === 'false') return false;
    if (button.classList.contains('is-active') || button.classList.contains('active')) return true;
    if (ctx.handle) return !readCanonical().includes(ctx.handle);
    if (ctx.id) return !legacyContainsId(ctx.id);
    return null;
  };

  const syncButton = (button, favourites) => {
    if (!button || button.matches(IGNORE_SELECTOR)) return;
    const ctx = extractContext(button);
    if (!ctx.handle && !ctx.id) return;

    const active = ctx.handle
      ? favourites.has(ctx.handle)
      : legacyContainsId(ctx.id);

    if (button.hasAttribute('aria-pressed')) {
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    }
    button.classList.toggle('is-active', active);

    const label = button.getAttribute('aria-label') || '';
    if (/favourites?|favorites?/i.test(label)) {
      const productPart = ctx.title ? ` ${ctx.title}` : '';
      button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'}${productPart} ${active ? 'from' : 'to'} favourites`.replace(/\s+/g, ' ').trim());
    }
  };

  function syncButtons() {
    /* UI sync must be read-only. Do not rebuild canonical favourites from
       currently rendered button states here: a stale aria-pressed="true"
       elsewhere on the page can otherwise re-add a product immediately after
       the customer removes it from the Quick Add drawer. Legacy state is
       migrated once during init; after that the canonical handle store is the
       source of truth. */
    const favourites = new Set(readCanonical());
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => syncButton(button, favourites));
  }

  const pressedValue = (button) => {
    if (!button) return null;
    const pressed = button.getAttribute('aria-pressed');
    if (pressed === 'true') return true;
    if (pressed === 'false') return false;
    if (button.classList.contains('is-active') || button.classList.contains('active')) return true;
    return null;
  };

  const handleFavouriteClick = (event) => {
    const target = event.target && typeof event.target.closest === 'function'
      ? event.target.closest(BUTTON_SELECTOR)
      : null;
    if (!target || target.matches(IGNORE_SELECTOR)) return;

    /* Capture the state BEFORE section-specific click handlers run. This lets
       the universal script support both kinds of buttons:
       1. sections that already toggle their own local wishlist state, and
       2. simple Quick Add bookmark buttons that rely on this universal file. */
    const beforeCtx = extractContext(target);
    const beforeHandle = normalizeHandle(beforeCtx.handle);
    const beforeCanonicalActive = beforeHandle ? readCanonical().includes(beforeHandle) : false;
    const beforeLegacyActive = beforeCtx.id ? legacyContainsId(beforeCtx.id) : false;
    const beforePressed = pressedValue(target);

    window.setTimeout(() => {
      const ctx = extractContext(target);
      const handle = normalizeHandle(ctx.handle);
      if (!handle) {
        syncButtons();
        return;
      }

      const canonicalActive = readCanonical().includes(handle);
      const legacyActive = ctx.id ? legacyContainsId(ctx.id) : false;
      const afterPressed = pressedValue(target);

      let active;

      /* If a section-specific handler already changed canonical storage,
         respect that result instead of toggling it a second time. */
      if (canonicalActive !== beforeCanonicalActive) {
        active = canonicalActive;
      }
      /* Older Picco sections may only change aria-pressed / is-active. */
      else if (afterPressed != null && beforePressed != null && afterPressed !== beforePressed) {
        active = afterPressed;
      }
      /* Other older sections only update one of the legacy ID stores. */
      else if (ctx.id && legacyActive !== beforeLegacyActive) {
        active = legacyActive;
      }
      /* No local handler changed anything: perform the actual universal
         toggle. This is the path used by the bookmark over Add to bag. */
      else {
        active = !beforeCanonicalActive;
      }

      setFavourite({ ...ctx, handle }, active, true);
    }, 0);
  };

  let observerTimer = 0;
  const queueSync = () => {
    window.clearTimeout(observerTimer);
    observerTimer = window.setTimeout(syncButtons, 30);
  };

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes && mutation.addedNodes.length)) queueSync();
  });

  document.addEventListener('click', handleFavouriteClick, true);
  document.addEventListener('shopify:section:load', queueSync);
  document.addEventListener('shopify:section:reorder', queueSync);
  document.addEventListener('picco:wishlist:change', queueSync);
  document.addEventListener('picco:favourites:changed', queueSync);
  window.addEventListener('storage', (event) => {
    if ([CANONICAL_KEY, DETAIL_KEY, ...LEGACY_ID_KEYS].includes(event.key)) queueSync();
  });

  const init = () => {
    /* One-time migration of any existing Picco legacy saves that can be
       resolved from visible product controls/details. From this point onward
       the canonical handle store is authoritative so remove actions cannot be
       resurrected by stale DOM state. */
    const migratedHandles = mergeVisibleLegacySaves();
    const detailHandles = readDetails().map((item) => item.handle).filter(Boolean);
    writeCanonical([...migratedHandles, ...detailHandles]);
    syncButtons();
    if (document.documentElement) {
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  };

  window.__PICCO_FAVOURITES_UNIVERSAL__ = {
    key: CANONICAL_KEY,
    detailKey: DETAIL_KEY,
    legacyKeys: LEGACY_ID_KEYS.slice(),
    read: readCanonical,
    set: (handle, active, extra = {}) => setFavourite({ ...extra, handle }, !!active, true),
    toggle: (handle, extra = {}) => {
      const clean = normalizeHandle(handle);
      if (!clean) return false;
      const active = !readCanonical().includes(clean);
      setFavourite({ ...extra, handle: clean }, active, true);
      return active;
    },
    sync: queueSync,
    extractContext
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
